import React from 'react'
import { Helmet } from 'react-helmet-async'
import { IPageConfig } from '@typescript'
import { Validators } from '@shared/utils/validators'
import { filterProps } from '@shared/utils/helmetUtils'

export interface PageMetaProps {
  gtmId?: string
  links?: IPageConfig['links']
  meta?: IPageConfig['meta']
  scripts?: IPageConfig['scripts']
}

// Small reusable helpers
const buildMeta = (name: string, val?: string) =>
  val ? (
    <meta
      content={val}
      key={name}
      name={name}
    />
  ) : null

const buildOG = (prop: string, val?: string) =>
  val ? (
    <meta
      content={val}
      key={prop}
      property={prop}
    />
  ) : null

const PageMeta: React.FC<PageMetaProps> = ({ gtmId, links, meta, scripts }) => {
  if (!meta) return null
  const { canonical, description, facebook, og, title, twitter } = meta

  const gtmScript = `
    (function(w,d,s,l,i){w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
      j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `

  return (
    <Helmet>
      {gtmId && Validators.isValidGTMId(gtmId) && (
        <>
          {/* <!-- Google Tag Manager --> */}
          <script data-body="true">{gtmScript}</script>
          <noscript>
            {`
          <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>
        `}
          </noscript>
          {/* <!-- End Google Tag Manager --> */}
        </>
      )}

      {/* Basic */}
      <title>{title || 'Loading'}</title>
      {description && buildMeta('description', description)}
      {canonical && (
        <link
          rel="canonical"
          href={canonical}
        />
      )}

      {/* Links */}
      {links?.map((l) => (
        <link
          key={l.href}
          {...filterProps(l)}
        />
      ))}

      {/* Facebook */}
      {facebook?.appId && buildMeta('facebook:app_id', facebook.appId)}

      {/* Open Graph */}
      {og &&
        Object.entries({
          'og:description': og.description,
          'og:image:url': og.image?.url,
          'og:image:secure_url': og.image?.secureUrl,
          'og:image:alt': og.image?.alt,
          'og:image:width': og.image?.width?.toString(),
          'og:image:height': og.image?.height?.toString(),
          'og:locale': og.locale,
          'og:site_name': og.siteName,
          'og:title': og.title,
          'og:type': og.type,
          'og:url': og.url,
        }).map(([prop, val]) => buildOG(prop, val))}

      {/* Twitter */}
      {twitter &&
        Object.entries({
          'twitter:app:id:googleplay': twitter.app?.id?.googlePlay,
          'twitter:app:id:iphone': twitter.app?.id?.iphone,
          'twitter:app:id:ipad': twitter.app?.id?.ipad,
          'twitter:app:name:googleplay': twitter.app?.name?.googlePlay,
          'twitter:app:name:iphone': twitter.app?.name?.iphone,
          'twitter:app:name:ipad': twitter.app?.name?.ipad,
          'twitter:app:url:googleplay': twitter.app?.url?.googlePlay,
          'twitter:app:url:iphone': twitter.app?.url?.iphone,
          'twitter:app:url:ipad': twitter.app?.url?.ipad,
          'twitter:card': twitter.card,
          'twitter:description': twitter.description,
          'twitter:image': twitter.image,
          'twitter:image:alt': twitter.imageAlt,
          'twitter:site': twitter.site,
          'twitter:title': twitter.title,
        }).map(([name, val]) => buildMeta(name, val))}

      {/* Head Scripts */}
      {scripts?.head?.map((s) => (
        <script
          key={s.src}
          {...filterProps(s)}
        />
      ))}

      {/* Body Scripts */}
      {scripts?.body?.map((s) => (
        <script
          data-body="true"
          key={s.src}
          {...filterProps(s)}
        />
      ))}

      {/* JSON-LD Schema */}
      {meta?.schema?.length &&
        meta.schema
          .filter((s): s is string => typeof s === 'string')
          .map((s, key) => (
            <script
              // eslint-disable-next-line react/no-array-index-key
              key={`schema-${key}`}
              type="application/ld+json">
              {s}
            </script>
          ))}
    </Helmet>
  )
}

export default PageMeta
